/** Land sections on place, animal and equipment pages, the move detail view and the ranch settings panel. */
import { useId, useState } from "react";
import { Link } from "react-router";
import { UNIT_LABELS, type AnimalDetail, type EventDTO, type Quantity, type SubjectRef } from "../../../contract";
import { openRecord } from "../../../web/forms/EventForm";
import { FieldInput, type Errors } from "../../../web/forms/SchemaForm";
import { RefPicker, useLabels } from "../../../web/forms/pickers";
import { api } from "../../../web/lib/api";
import { formatDate, formatNumber, relativeDay, useFormat } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { invalidateAll, useApi } from "../../../web/lib/query";
import { useCan, useMe } from "../../../web/lib/session";
import { registry } from "../../../web/registry";
import { Button, Card, Disclosure, Empty, ErrorNote, Field, List, Loading, Row, Section, Stat, TextInput, toast } from "../../../web/ui/kit";
import { DataList } from "../../../web/shell/describe";
import { EventRow } from "../../../web/shell/Timeline";
import type { LocationState, PlaceLandState } from "../contract";
import { BudgetNote, GRAZED, StageChip, WaterChip, daysText, rainText, usePlaceGrazing } from "./shared";

type Place = Record<string, any>;

const placeRef = (p: Place, role: string): SubjectRef => ({ kind: "place", id: p.id, role, label: p.name });

// ---------------------------------------------------------------- place: grazing

export function showGrazing(p: Place): boolean {
  return GRAZED.has(p.kind) || p.kind === "pen" || !!p.facets?.grazing;
}

export function GrazingSection({ entity }: { entity: Place }) {
  useLanguage();
  const q = usePlaceGrazing(entity.id);
  const can = useCan();
  const { data: me } = useMe();
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorNote error={q.error} retry={() => q.refetch()} />;
  const g = q.data;
  const s = g.status;
  const grazed = GRAZED.has(g.kind);
  const areaLabel = s.area ? `${formatNumber(s.area.value, 0)} ${t(UNIT_LABELS[s.area.unit] ?? s.area.unit)}` : null;
  const restLine = s.head > 0
    ? (s.occupied_since ? t("In use {{days}}", { days: daysText(Math.max(0, dayDiff(s.occupied_since, g.today))) }) : t("In use"))
    : s.rest_days !== null ? (s.rest_target ? t("Rested {{rest}} of {{target}} days", { rest: s.rest_days, target: s.rest_target }) : t("Rested {{days}}", { days: daysText(s.rest_days) })) : t("No grazing recorded");
  return (
    <div className="stack land-place">
      <div className="land-stats">
        <Stat label={t("Head here")} value={s.head} hint={s.land?.counted ? (s.land.counted.unit === "pairs" ? plural(s.land.counted.head, "plus {{count}} pair counted", "plus {{count}} pairs counted") : plural(s.land.counted.head, "plus {{count}} head counted", "plus {{count}} head counted")) : undefined} />
        {grazed && <Stat label={s.head > 0 ? t("In use") : t("Rest")} value={s.head > 0 ? (s.occupied_since ? daysText(dayDiff(s.occupied_since, g.today)) : "—") : s.rest_days !== null ? daysText(s.rest_days) : "—"} hint={s.head === 0 && s.rest_target ? t("target {{days}}", { days: daysText(s.rest_target) }) : undefined} tone={s.head === 0 && s.rest_target && s.rest_days !== null && s.rest_days >= s.rest_target ? "good" : undefined} />}
        <Stat label={t("Animal-unit days this month")} value={formatNumber(s.aud_month, 0)} hint={t("estimate")} />
        {areaLabel && <Stat label={t(s.area!.basis === "usable" ? "Grazable area" : s.area!.basis === "mapped" ? "Mapped area" : "Area")} value={areaLabel} hint={s.area!.basis === "mapped" ? t("calculated from the map") : undefined} />}
      </div>
      {can("record") && (
        <div className="row-actions">
          <Button variant="primary" icon="move" onClick={() => openRecord({ initial: { type: "land.move", subjects: [placeRef(g, "to")] } })}>{t("Move cattle here")}</Button>
          {s.head > 0 && <Button icon="move" onClick={() => openRecord({ initial: { type: "land.move", subjects: [placeRef(g, "from")] } })}>{t("Move cattle out")}</Button>}
          {grazed && <Button icon="grass" onClick={() => openRecord({ initial: { type: "land.forage", subjects: [placeRef(g, "place")] } })}>{t("Check grass")}</Button>}
          <Button icon="water" onClick={() => openRecord({ initial: { type: "land.water_check", subjects: [placeRef(g, "place")] } })}>{t("Check water")}</Button>
        </div>
      )}
      <Card>
        <p className="land-rest-line">{restLine}</p>
        {s.groups.length > 0 && <ul className="land-groups">{s.groups.map((x) => <li key={x.id}><Link to={`/cattle/groups/${x.id}`}>{x.name}</Link><span>{plural(x.head, "{{count}} head", "{{count}} head")}</span></li>)}{s.ungrouped > 0 && <li><span>{t("Not in a group")}</span><span>{plural(s.ungrouped, "{{count}} head", "{{count}} head")}</span></li>}</ul>}
        {s.land?.water && <p className="land-water-line">{t("Water")}: <WaterChip state={s.land.water.worst} /> <span className="muted">{relativeDay(s.land.water.on, g.today)}</span></p>}
        {s.land?.work?.off_until && s.land.work.off_until > g.today && <p className="land-water-line">{t("Keep cattle off until {{date}}", { date: formatDate(s.land.work.off_until) })}</p>}
      </Card>

      {g.plans.length > 0 && (
        <Section title={t("Planned here")}>
          <List>{g.plans.map((p) => <Row key={`${p.plan.id}${p.stage_id}`} icon="route" to={`/work/jobs/${p.plan.id}`} title={p.plan.label} meta={[formatDate(p.start), daysText(p.days)].join(" · ")} trailing={<StageChip status={p.status} />} />)}</List>
        </Section>
      )}

      {grazed && (
        <Section title={t("Grass")} action={can("record") && <Button size="sm" variant="ghost" icon="plus" onClick={() => openRecord({ initial: { type: "land.forage", subjects: [placeRef(g, "place")] } })}>{t("Check grass")}</Button>}>
          {g.forage.length ? <List>{g.forage.slice(0, 5).map((f) => <Row key={f.id} icon="grass" to={`/history/${f.id}`} title={f.summary ?? t("Forage check")} meta={formatDate(f.on)} />)}</List> : <p className="muted">{t("No forage checks yet.")}</p>}
          <BudgetNote budget={g.budget} units={me?.ranch?.units} />
        </Section>
      )}

      <Section title={t("Grazing history")}>
        {g.episodes.length === 0 ? <p className="muted">{t("No moves in or out recorded yet.")}</p> : (
          <List>{g.episodes.map((ep) => (
            <Row key={ep.from} icon="calendar" title={ep.to ? t("{{from}} to {{to}}", { from: formatDate(ep.from), to: formatDate(ep.to) }) : t("Since {{date}}", { date: formatDate(ep.from) })}
              meta={[daysText(ep.days), plural(ep.head, "{{count}} head", "{{count}} head"), t("{{aud}} AUD (estimate)", { aud: formatNumber(ep.aud, 0) })].join(" · ")} />
          ))}</List>
        )}
      </Section>

      {g.water.length > 0 && (
        <Section title={t("Water checks")}><ol className="timeline-list">{g.water.slice(0, 5).map((e) => <EventRow key={e.id} event={e} showSubjects={false} />)}</ol></Section>
      )}

      {can("manage") && <PlaceSettings id={g.id} kind={g.kind} attrs={g.attrs} fallback={g.rest_target_default} mapped={g.area_mapped_ac} onSaved={() => void q.refetch()} />}
    </div>
  );
}

function dayDiff(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from.slice(0, 10)}T12:00:00Z`)) / 86_400_000);
}

function PlaceSettings({ id, kind, attrs, fallback, mapped, onSaved }: { id: string; kind: string; attrs: { rest_target_days?: number; usable_area?: Quantity; forage_type?: string; premises_id?: string }; fallback: number | null; mapped: number | null; onSaved: () => void }) {
  const [rest, setRest] = useState(attrs.rest_target_days !== undefined ? String(attrs.rest_target_days) : "");
  const [usable, setUsable] = useState<Quantity | undefined>(attrs.usable_area);
  const [forage, setForage] = useState(attrs.forage_type ?? "");
  const [premises, setPremises] = useState(attrs.premises_id ?? "");
  const [busy, setBusy] = useState(false);
  const fields = registry.module("land")!.attributes!.place!;
  const errors: Errors = {};
  const ids = { rest: useId(), forage: useId(), premises: useId() };
  return (
    <Disclosure label={t("Grazing settings")}>
      {GRAZED.has(kind) && (
        <Field label={t("Rest target (days)")} optional htmlFor={ids.rest} hint={fallback ? t("Leave empty to use the ranch's {{days}}.", { days: daysText(fallback) }) : t("How long this pasture should rest before cattle go back in.")}>
          <TextInput id={ids.rest} type="number" inputMode="numeric" min={0} value={rest} onChange={(e) => setRest(e.target.value)} />
        </Field>
      )}
      <FieldInput name="usable_area" field={fields.usable_area} value={usable} onChange={(v) => setUsable(v as Quantity | undefined)} errors={errors} />
      {mapped && <p className="field-hint">{t("The map outline is about {{acres}} acres.", { acres: formatNumber(mapped, 0) })}</p>}
      <Field label={t("Main forage")} optional htmlFor={ids.forage}><TextInput id={ids.forage} value={forage} onChange={(e) => setForage(e.target.value)} /></Field>
      <Field label={t("Premises ID")} optional htmlFor={ids.premises} hint={t("An official premises or location ID, kept exactly as written.")}><TextInput id={ids.premises} className="mono" value={premises} onChange={(e) => setPremises(e.target.value)} /></Field>
      <Button busy={busy} icon="check" onClick={async () => {
        setBusy(true);
        try {
          await api.put(`/m/land/places/${id}/settings`, { rest_target_days: rest ? Number(rest) : null, usable_area: usable ?? null, forage_type: forage || null, premises_id: premises || null });
          await invalidateAll();
          onSaved();
          toast(t("Saved"), { tone: "good" });
        } catch (err) {
          toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" });
        } finally { setBusy(false); }
      }}>{t("Save settings")}</Button>
    </Disclosure>
  );
}

// ---------------------------------------------------------------- place: rain

export function showRain(p: Place): boolean {
  return !!(p.facets?.land as PlaceLandState | undefined)?.rain;
}

export function RainSection({ entity }: { entity: Place }) {
  useLanguage();
  const q = usePlaceGrazing(entity.id);
  const can = useCan();
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorNote error={q.error} />;
  const r = q.data.rain;
  const max = Math.max(1, ...r.months.map((m) => m.total));
  return (
    <div className="stack">
      <div className="land-stats">
        <Stat label={t("Last 12 months")} value={rainText(r.total, r.unit)} />
        <Stat label={t("Readings")} value={r.readings.length} />
      </div>
      {can("record") && <Button icon="rain" onClick={() => openRecord({ initial: { type: "land.rainfall", subjects: [placeRef(entity, "place")] } })}>{t("Record rain here")}</Button>}
      {r.months.length > 0 && (
        <div className="land-bars" role="list">
          {r.months.map((m) => (
            <div key={m.month} className="land-bar" role="listitem" aria-label={`${formatDate(m.month, "month")}: ${rainText(m.total, r.unit)}`}>
              <span className="land-bar-value">{formatNumber(m.total, r.unit === "mm" ? 0 : 1)}</span>
              <span className="land-bar-fill" style={{ height: `${Math.max(4, (m.total / max) * 100)}%` }} />
              <span className="land-bar-label">{formatDate(m.month, "month").slice(0, 3)}</span>
            </div>
          ))}
        </div>
      )}
      <List>{r.readings.slice(0, 15).map((x) => <Row key={x.id} icon="rain" to={`/history/${x.id}`} title={rainText(x.amount, r.unit)} meta={[formatDate(x.on), x.since ? t("since {{date}}", { date: formatDate(x.since) }) : null, x.estimate ? t("estimate") : null].filter(Boolean).join(" · ")} />)}</List>
    </div>
  );
}

// ---------------------------------------------------------------- place: hay and field work

export function showHayWork(p: Place): boolean {
  const land = p.facets?.land as PlaceLandState | undefined;
  return p.kind === "field" || !!land?.hay || !!land?.work;
}

export function HayWorkSection({ entity }: { entity: Place }) {
  useLanguage();
  const q = usePlaceGrazing(entity.id);
  const can = useCan();
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorNote error={q.error} />;
  const land = q.data.status.land;
  const year = q.data.today.slice(0, 4);
  return (
    <div className="stack">
      {land?.hay && (
        <div className="land-stats">
          <Stat label={t("Baled this year")} value={formatNumber(land.hay.years[year]?.bales ?? 0, 0)} hint={plural(land.hay.years[year]?.cuttings ?? 0, "{{count}} cutting", "{{count}} cuttings")} />
          {land.hay.years[String(Number(year) - 1)] && <Stat label={t("Last year")} value={formatNumber(land.hay.years[String(Number(year) - 1)].bales, 0)} hint={t("bales")} />}
        </div>
      )}
      {can("record") && (
        <div className="row-actions">
          <Button icon="hay" onClick={() => openRecord({ initial: { type: "land.hay_harvest", subjects: [placeRef(entity, "field")] } })}>{t("Record baling here")}</Button>
          <Button icon="tractor" onClick={() => openRecord({ initial: { type: "land.field_work", subjects: [placeRef(entity, "field")] } })}>{t("Record field work here")}</Button>
        </div>
      )}
      <Section title={t("Baling")}>{q.data.hay.length ? <ol className="timeline-list">{q.data.hay.map((e) => <EventRow key={e.id} event={e} showSubjects={false} />)}</ol> : <p className="muted">{t("No baling recorded here.")}</p>}</Section>
      <Section title={t("Field work")}>{q.data.work.length ? <ol className="timeline-list">{q.data.work.map((e) => <EventRow key={e.id} event={e} showSubjects={false} />)}</ol> : <p className="muted">{t("No field work recorded here.")}</p>}</Section>
    </div>
  );
}

// ---------------------------------------------------------------- animal: where it's been

export function showLocation(a: AnimalDetail): boolean {
  return ((a.facets.location as LocationState | undefined)?.history?.length ?? 0) > 1;
}

export function LocationSection({ animal }: { animal: AnimalDetail }) {
  useLanguage();
  const loc = animal.facets.location as LocationState | undefined;
  const history = [...(loc?.history ?? [])].reverse();
  const labels = useLabels(history.map((h) => ({ kind: "place", id: h.place_id })));
  const today = new Date().toISOString().slice(0, 10);
  if (!history.length) return <Empty icon="place" title={t("No moves recorded")} />;
  return (
    <List>
      {history.map((h, i) => (
        <Row key={`${h.event_id}${i}`} icon="place" tone={h.to ? undefined : "land"} to={`/ranch/places/${h.place_id}`} title={labels[h.place_id] ?? "…"}
          meta={h.to ? `${t("{{from}} to {{to}}", { from: formatDate(h.from), to: formatDate(h.to) })} · ${daysText(dayDiff(h.from, h.to))}` : `${t("Since {{date}}", { date: formatDate(h.from) })} · ${daysText(Math.max(0, dayDiff(h.from, today)))}`} />
      ))}
    </List>
  );
}

// ---------------------------------------------------------------- equipment: water

export function showAssetWater(a: Record<string, any>): boolean {
  return a.kind === "water" || !!a.facets?.land;
}

export function AssetWaterSection({ entity }: { entity: Record<string, any> }) {
  useLanguage();
  const can = useCan();
  const w = (entity.facets?.land as { water?: { on: string; state: "ok" | "low" | "empty" | "broken"; level?: number } } | undefined)?.water;
  const today = new Date().toISOString().slice(0, 10);
  return (
    <Card>
      {w ? <p className="land-water-line"><WaterChip state={w.state} />{w.level !== undefined ? ` ${w.level}%` : ""} <span className="muted">{t("Checked {{when}}", { when: relativeDay(w.on, today) })}</span></p> : <p className="muted">{t("No water checks yet.")}</p>}
      {can("record") && <Button icon="water" onClick={() => openRecord({ initial: { type: "land.water_check", subjects: [{ kind: "asset", id: entity.id, role: "water", label: entity.name }, ...(entity.place_id ? [{ kind: "place" as const, id: entity.place_id, role: "place" }] : [])] } })}>{t("Check water")}</Button>}
    </Card>
  );
}

// ---------------------------------------------------------------- move detail

export function MoveDetail({ event }: { event: EventDTO }) {
  useLanguage();
  const fmt = useFormat(event.labels);
  const def = registry.event("land.move")!;
  const impact = useApi<{ animals: number; changes: Array<{ count: number; now: { id: string; label: string } | null; instead: { id: string; label: string } | null }> }>(event.status === "active" ? `/m/land/moves/${event.id}/impact` : null);
  const animals = event.subjects.filter((s) => s.kind === "animal").length;
  return (
    <div className="stack">
      <Card>
        <dl className="data-list">
          <div><dt>{t("Cattle")}</dt><dd>{animals ? plural(animals, "{{count}} head", "{{count}} head") : t("Counted, not named")}</dd></div>
        </dl>
        <DataList fields={def.fields} data={event.data} fmt={fmt} />
      </Card>
      {impact.data && impact.data.changes.length > 0 && (
        <Card className="land-impact">
          <span className="eyebrow">{t("If this move is removed")}</span>
          <ul>{impact.data.changes.map((c, i) => (
            <li key={i}>{c.instead ? t("{{count}} head would show back in {{place}}", { count: c.count, place: c.instead.label }) : t("{{count}} head would have no place recorded", { count: c.count })}</li>
          ))}</ul>
          <p className="muted">{t("Pasture rest and grazing days are worked out again from the moves that remain.")}</p>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- ranch settings

export function LandSettings() {
  useLanguage();
  const { data: me } = useMe();
  const can = useCan();
  const current = (me?.ranch?.settings.land ?? {}) as { rest_target_days?: number; utilization_pct?: number; intake_lb?: number; main_gauge?: string };
  const [rest, setRest] = useState(current.rest_target_days !== undefined ? String(current.rest_target_days) : "");
  const [util, setUtil] = useState(current.utilization_pct !== undefined ? String(current.utilization_pct) : "");
  const [intake, setIntake] = useState(current.intake_lb !== undefined ? String(current.intake_lb) : "");
  const [gauge, setGauge] = useState<string | null>(current.main_gauge ?? null);
  const [busy, setBusy] = useState(false);
  const fid = { rest: useId(), gauge: useId(), util: useId(), intake: useId() };
  if (!can("manage") || !me?.ranch) return null;
  const num = (s: string) => (s.trim() ? Number(s) : undefined);
  return (
    <Section title={t("Grazing and rain")}>
      <Card>
        <Field label={t("Rest target for pastures (days)")} optional htmlFor={fid.rest} hint={t("Used for any pasture without its own target.")}><TextInput id={fid.rest} type="number" inputMode="numeric" min={0} value={rest} onChange={(e) => setRest(e.target.value)} /></Field>
        <Field label={t("Main rain gauge")} optional htmlFor={fid.gauge} hint={t("Where rain is counted for the ranch when a reading names no place.")}><RefPicker id={fid.gauge} target="place" value={gauge} onChange={(v) => setGauge(v as string | null)} /></Field>
        <Disclosure label={t("Forage estimate assumptions")}>
          <Field label={t("Planned use of standing forage")} optional htmlFor={fid.util} hint={t("Take half, leave half is 50%.")}><div className="input-suffix"><TextInput id={fid.util} type="number" inputMode="numeric" min={1} max={100} value={util} onChange={(e) => setUtil(e.target.value)} /><span className="suffix">%</span></div></Field>
          <Field label={t("Daily forage per animal unit (lb)")} optional htmlFor={fid.intake} hint={t("26 lb of dry forage is a common figure for a 1,000 lb cow.")}><TextInput id={fid.intake} type="number" inputMode="decimal" min={1} max={100} value={intake} onChange={(e) => setIntake(e.target.value)} /></Field>
        </Disclosure>
        <Button busy={busy} icon="check" onClick={async () => {
          setBusy(true);
          try {
            const land = Object.fromEntries(Object.entries({ rest_target_days: num(rest), utilization_pct: num(util), intake_lb: num(intake), main_gauge: gauge ?? undefined }).filter(([, v]) => v !== undefined));
            await api.patch("/ranch", { settings: { land } });
            await api.post("/m/land/recompute");
            await invalidateAll();
            toast(t("Saved"), { tone: "good" });
          } catch (err) {
            toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" });
          } finally { setBusy(false); }
        }}>{t("Save")}</Button>
      </Card>
    </Section>
  );
}

